import { type ReactNode, createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { formatPlaceholderToken } from '../html-placeholders';
import type { VisualEditorCommand, VisualEditorSelectionStyle } from './editor-types';

type InsertHandler = (_token: string) => void;
type CommandHandler = (_command: VisualEditorCommand) => void;
type FontSizeSelectionHandler = (_fontSize: string | undefined) => void;

export interface HtmlTemplateEditorApi {
    insertPlaceholder(_placeholder: string): void;
    executeCommand(_command: VisualEditorCommand): void;
    selectionStyle: VisualEditorSelectionStyle;
    registerInsertHandler(_handler: InsertHandler | null): void;
    registerCommandHandler(_handler: CommandHandler | null): void;
    registerFontSizeSelectionHandler(_handler: FontSizeSelectionHandler | null): void;
    notifyFontSizeSelection(_fontSize: string | undefined): void;
    setSelectionStyle(_style: VisualEditorSelectionStyle): void;
}

const HtmlTemplateEditorContext = createContext<HtmlTemplateEditorApi | null>(null);

export const HtmlTemplateEditorProvider = (props: { children: ReactNode }) => {
    const insertHandlerRef = useRef<InsertHandler | null>(null);
    const commandHandlerRef = useRef<CommandHandler | null>(null);
    const fontSizeSelectionHandlerRef = useRef<FontSizeSelectionHandler | null>(null);
    const [selectionStyle, setSelectionStyle] = useState<VisualEditorSelectionStyle>({});

    const insertPlaceholder = useCallback((placeholder: string) => {
        insertHandlerRef.current?.(formatPlaceholderToken(placeholder));
    }, []);

    const executeCommand = useCallback((command: VisualEditorCommand) => {
        commandHandlerRef.current?.(command);
    }, []);

    const registerInsertHandler = useCallback((handler: InsertHandler | null) => {
        insertHandlerRef.current = handler;
    }, []);

    const registerCommandHandler = useCallback((handler: CommandHandler | null) => {
        commandHandlerRef.current = handler;
    }, []);

    const registerFontSizeSelectionHandler = useCallback((handler: FontSizeSelectionHandler | null) => {
        fontSizeSelectionHandlerRef.current = handler;
    }, []);

    const notifyFontSizeSelection = useCallback((fontSize: string | undefined) => {
        fontSizeSelectionHandlerRef.current?.(fontSize);
    }, []);

    const value = useMemo<HtmlTemplateEditorApi>(
        () => ({
            insertPlaceholder,
            executeCommand,
            selectionStyle,
            registerInsertHandler,
            registerCommandHandler,
            registerFontSizeSelectionHandler,
            notifyFontSizeSelection,
            setSelectionStyle
        }),
        [
            executeCommand,
            insertPlaceholder,
            notifyFontSizeSelection,
            registerCommandHandler,
            registerFontSizeSelectionHandler,
            registerInsertHandler,
            selectionStyle
        ]
    );

    return <HtmlTemplateEditorContext.Provider value={value}>{props.children}</HtmlTemplateEditorContext.Provider>;
};

export const useHtmlTemplateEditor = (): HtmlTemplateEditorApi => {
    const context = useContext(HtmlTemplateEditorContext);
    if (!context) {
        throw new Error('useHtmlTemplateEditor must be used within HtmlTemplateEditorProvider');
    }
    return context;
};
